import { z } from "zod";
import { SCHEMA_VERSION } from "./version.js";
import { imageEmbeddingSchema } from "./embedding.js";
import { propertiesSchema } from "./properties.js";
import { searchTextSchema } from "./searchable-text.js";
/** Closed UI suggestion set; storage stays open (ADR-0003). */
export const KIND_SUGGESTIONS = [
    "box",
    "rack",
    "room",
    "floor",
    "level",
    "drawer",
];
export const FILE_KINDS = ["photo", "audio"];
/**
 * Capture location of a photo (ticket 04): full precision at rest — the
 * display form ("52.52° N 13.41° E") is derived in the UI, never stored.
 */
export const fileLocationSchema = z.object({
    latitude: z.number(),
    longitude: z.number(),
    capturedAt: z.string().datetime(),
});
export const fileEntrySchema = z.object({
    id: z.string().regex(/^[a-f0-9]{32}$/),
    kind: z.enum(FILE_KINDS),
    name: z.string().min(1),
    mime: z.string().min(1),
    bytes: z.number().int().nonnegative(),
    createdAt: z.string().datetime(),
    filename: z.string().min(1),
    location: fileLocationSchema.nullable().default(null),
    // Pipeline enrichment outputs (ADR-0014)
    data: z.object({
        preview: z.object({
            files: z.array(z.string())
        }).optional(),
        stt: z.object({
            text: z.string(),
            at: z.string().datetime(),
            model: z.string()
        }).optional(),
        // Human correction of the auto transcript (ADR-0035). Present — even with
        // empty text — means the override is active; absent falls back to `stt`.
        manualText: z.object({
            text: z.string(),
            at: z.string().datetime()
        }).optional(),
        embedding: imageEmbeddingSchema.optional(),
    }).optional(),
});
/** LLM-generated summary on a container (Phase 2 / ticket 06). */
export const llmSummarySourceSchema = z.object({
    kind: z.enum(["description", "transcript", "detection"]),
    ref: z.string(),
    at: z.string().datetime(),
});
export const llmSummaryDataSchema = z.object({
    owner: z.string().optional(),
    summary: z.string(),
});
export const llmSummarySchema = z.object({
    generatedAt: z.string().datetime(),
    model: z.string(),
    sources: z.array(llmSummarySourceSchema),
    transcript: z.string(),
    data: llmSummaryDataSchema,
});
/**
 * One machine-detected observation about a container's media (PROJECT.md
 * phase 4). The extract stage reads `data.detections` as its third fusion
 * source; no producer ships yet, so the field is optional and an empty list
 * simply means "no detection events yet". `id`/(`at`) become the
 * `sources[].kind === "detection"` ref/timestamp, which is what the extract
 * idempotency key hashes.
 */
export const detectionResultSchema = z.object({
    id: z.string().min(1),
    at: z.string().datetime(),
    label: z.string().min(1),
    confidence: z.number().min(0).max(1).optional(),
});
/**
 * What the apply stage last copied from a summary into the scalar `owner` /
 * `description` fields. Field present iff the corresponding scalar currently
 * holds an AI-filled value, which is what lets a later summary refresh its own
 * output without ever touching a manual value.
 */
export const aiAppliedSchema = z.object({
    owner: z.string().optional(),
    summary: z.string().optional(),
});
/**
 * The on-disk box.json shape (ADR-0003, schemaVersion 1). No `parentId` is
 * stored — a container's parent is derived by reverse lookup on `children`.
 * Exported as a plain object schema so callers can `.extend` its shape (e.g.
 * the move response); `containerSchema` below applies the explicit output type
 * so defaulted fields are required in the parsed result (the runtime always
 * applies them).
 */
export const containerObjectSchema = z.object({
    schemaVersion: z.literal(SCHEMA_VERSION),
    version: z.number().int().nonnegative(),
    id: z.string().uuid(),
    kind: z.string().min(1),
    name: z.string().min(1),
    description: z.string().default(""),
    owner: z.string().optional(),
    tags: z.array(z.string()).default([]),
    /** Typed custom properties (ADR-0037); optional on legacy records and
     *  coerced to `{}` when the catalog store loads container.json. Declared
     *  here so the frontend's Zod parse does not silently strip the field. */
    properties: propertiesSchema.optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    deleted: z.boolean().default(false),
    deletedAt: z.string().datetime().nullable().default(null),
    children: z.array(z.string().uuid()).default([]),
    files: z.array(fileEntrySchema).default([]),
    // Pipeline enrichment outputs (ADR-0015)
    data: z.object({
        llmSummary: llmSummarySchema.optional(),
        aiApplied: aiAppliedSchema.optional(),
        searchText: searchTextSchema.optional(),
        detections: z.array(detectionResultSchema).optional(),
    }).optional(),
});
export const containerSchema = containerObjectSchema;
/**
 * Payload a client sends to create or update a container's scalar fields.
 * Tree placement is handled by the child-scoped tree routes, not here.
 * Optional fields mirror a form (empty strings/tags are omitted).
 */
export const containerInputSchema = z.object({
    kind: z.string().min(1, "kind is required"),
    name: z.string().min(1, "name is required"),
    description: z.string().optional(),
    owner: z.string().optional(),
    tags: z.array(z.string()).optional(),
    /** Typed custom properties (ADR-0037); replaced wholesale like item edits. */
    properties: propertiesSchema.optional(),
});
/**
 * Create payload (ticket 30): optionally carries the container's UUID, so a
 * pre-printed label's QR code can become the container's identity. The API
 * rejects an id that already exists with 409.
 */
export const containerCreateSchema = containerInputSchema.extend({
    id: z.string().uuid().optional(),
    /**
     * The catalog entry to add the container's root to (catalog-entry-assignment
     * 02). Omitted means the default entry. Only affects containers created
     * without a parent (new roots); nested containers inherit their parent's
     * entry.
     */
    entryId: z.string().uuid().optional(),
});
/**
 * Tags are case-normalized on save so the same tag is never stored twice in
 * different spellings (ticket 24): trimmed, lowercased, deduplicated.
 */
export function normalizeTags(tags) {
    return [...new Set(tags.map((t) => t.trim().toLowerCase()).filter(Boolean))];
}
/** An edit carries the scalar fields plus the version it was built from. */
export const containerUpdateSchema = containerInputSchema.extend({
    version: z.number().int().nonnegative(),
});
/** Soft-delete carries the version the delete decision was built from. */
export const deleteContainerSchema = z.object({
    version: z.number().int().nonnegative(),
    /**
     * Required when the container has children: the API refuses to delete a
     * container without this confirmation, because children are unlinked
     * (ticket 08). Idempotent for childless containers.
     */
    confirmChildren: z.boolean().optional(),
});
/**
 * `POST /api/containers/:id/re-extract` (LLM re-summary): the container's live
 * version, which the version-neutral trigger leaves unchanged (ticket 10). The
 * route deliberately returns no block: the merge runs asynchronously and the
 * regenerated version arrives through SSE, not this response.
 */
export const reExtractResponseSchema = z.object({
    version: z.number().int().nonnegative(),
});
/**
 * `DELETE /api/containers/:id/files/:fileId/stt` — the transcript was dropped
 * to wake the STT pipeline; `version` is the container's bumped version.
 */
export const reTranscribeResponseSchema = z.object({
    sttDeleted: z.boolean(),
    version: z.number().int().nonnegative(),
});
/**
 * `DELETE /api/containers/:id/files/:fileId/preview` — the preview was dropped
 * to wake the preview pipeline; `version` is the container's bumped version.
 */
export const rePreviewResponseSchema = z.object({
    previewDeleted: z.boolean(),
    version: z.number().int().nonnegative(),
});
/**
 * Stable CSV export columns; added, never reordered or renamed (ADR-0003).
 * `expiresOn` was appended (deadlines 06) for insurance/archiving; the
 * narrower property vocabulary of ADR-0037 means it is the only date column.
 */
export const CSV_COLUMNS = [
    "id",
    "kind",
    "name",
    "description",
    "owner",
    "tags",
    "path",
    "createdAt",
    "updatedAt",
    "deleted",
    "expiresOn",
];
//# sourceMappingURL=container.js.map